import React, { useState, useMemo, useEffect } from 'react';
import cx from 'classnames';
import '../../common/DateFormat/index.js';
import '@anton.bobrov/react-hooks';
import '@anton.bobrov/react-components';
import '../../common/GradientWrapper/index.js';
import { Icon } from '../../common/Icon/index.js';
import '../../common/LazyImage/index.js';
import '../../common/NumberFormat/index.js';
import '../../common/WithCustomScrollbar/index.js';
import { copyToBuffer } from '../../../utils/copy-to-buffer.js';
import styles from './index.module.scss.js';

const icons = {
  password: "Password",
  email: "Mail",
  user: "Account"
};
const FormInputLabel = ({ blueBold, children, className, style }) => /* @__PURE__ */ React.createElement(
  "span",
  {
    className: cx(styles.label__inner, className, {
      [styles["blue-bold"]]: blueBold
    }),
    style
  },
  children
);
FormInputLabel.displayName = "FormInputLabel";
const FormInput = ({
  name,
  label,
  type,
  value,
  placeholder,
  required,
  disabled,
  numberType,
  errors,
  errorMsgAbsolute,
  onBlur = () => {
  },
  onChange = () => {
  },
  onFocus = () => {
  },
  onKeyPress = () => {
  },
  onKeyUp = () => {
  },
  icon,
  image,
  size = "md",
  prefix,
  limit = null,
  showIcon = true,
  maxButton,
  copyButton,
  className,
  style,
  smallLabel,
  innerRef,
  ...props
}) => {
  const [innerValue, setInnerValue] = useState(value);
  const [isHidePassword, setHidePassword] = useState(true);
  const changeHandler = (e) => {
    let v = e.target.value;
    if (numberType) {
      v = e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*?)\..*/g, "$1").replace(/^0[^.]/, "0");
      setInnerValue(v && v[0] === "." ? 0 + v : v);
      onChange(v && v[0] === "." ? 0 + v : v);
    } else if (limit && Array.from(e.target.value).length > limit) {
      e.target.value = e.target.value.slice(0, limit);
    } else {
      setInnerValue(v);
      onChange(v);
    }
  };
  const inputType = useMemo(() => {
    if (type === "password") {
      if (isHidePassword) {
        return type;
      }
      return "input";
    }
    return type;
  }, [isHidePassword, type]);
  const iconComponent = useMemo(() => {
    if (!showIcon) return null;
    if (image) {
      return image;
    }
    if (icons[type]) {
      return /* @__PURE__ */ React.createElement(Icon, { iconName: icons[type] });
    }
    if (icon) {
      return /* @__PURE__ */ React.createElement(Icon, { customIcon: icon });
    }
    return null;
  }, [showIcon, icon, image, type]);
  const isWithMax = useMemo(
    () => maxButton && type !== "password" && !copyButton,
    [copyButton, type, maxButton]
  );
  const isWithCopy = useMemo(
    () => !!copyButton && type !== "password" && !maxButton,
    [copyButton, type, maxButton]
  );
  useEffect(() => {
    setInnerValue(value);
  }, [value]);
  return /* @__PURE__ */ React.createElement(
    "div",
    {
      className: cx(styles.box, className, {
        [styles[size]]: !!size,
        [styles.required]: required,
        [styles.invalid]: !!errors,
        [styles[type]]: !!type,
        [styles["with-icon"]]: iconComponent,
        [styles["with-prefix"]]: !!prefix,
        [styles["with-copy"]]: isWithCopy,
        [styles["small-label"]]: smallLabel
      }),
      style
    },
    /* @__PURE__ */ React.createElement("div", { className: styles.box__inner }, label && /* @__PURE__ */ React.createElement("label", { htmlFor: name }, label), /* @__PURE__ */ React.createElement("div", { className: styles["input-wrapper"] }, prefix && /* @__PURE__ */ React.createElement("div", { className: styles.prefix }, prefix), /* @__PURE__ */ React.createElement(
      "input",
      {
        id: name,
        name,
        type: inputType,
        disabled,
        value: innerValue || "",
        onChange: changeHandler,
        onBlur,
        onFocus,
        onKeyPress,
        onKeyUp,
        "aria-label": name,
        onKeyDown: (e) => {
          if (type === "number" && (e.key === "e" || e.key === "E" || e.key === "+" || e.key === "-")) {
            e.preventDefault();
          }
        },
        placeholder,
        autoComplete: "new-password",
        ref: innerRef,
        ...props
      }
    ), iconComponent, type === "password" && /* @__PURE__ */ React.createElement(
      "button",
      {
        type: "button",
        className: styles["show-password"],
        onClick: () => setHidePassword((prev) => !prev)
      },
      !isHidePassword ? /* @__PURE__ */ React.createElement(Icon, { iconName: "Eye" }) : /* @__PURE__ */ React.createElement(Icon, { iconName: "EyeOff" })
    ), isWithMax && /* @__PURE__ */ React.createElement(
      "button",
      {
        type: "button",
        className: styles["max-btn"],
        onClick: maxButton?.onMaxClick
      },
      maxButton?.text || "Max"
    ), isWithCopy && /* @__PURE__ */ React.createElement(
      "button",
      {
        type: "button",
        "aria-label": "copy",
        className: styles["copy-btn"],
        onClick: (e) => {
          e.preventDefault();
          e.stopPropagation();
          copyToBuffer(String(innerValue));
          copyButton?.onClick();
        }
      },
      /* @__PURE__ */ React.createElement(Icon, { iconName: "CopyIconDouble" })
    ))),
    errors && /* @__PURE__ */ React.createElement(
      "div",
      {
        className: cx(styles.error, {
          [styles.absolute]: errorMsgAbsolute
        })
      },
      errors.message
    )
  );
};
FormInput.displayName = "FormInput";

export { FormInput, FormInputLabel };
//# sourceMappingURL=index.js.map
